import React, { useRef, useState } from 'react';
import { Dot } from 'lucide-react';
import html2canvas from 'html2canvas';
import styled from 'styled-components';
import useToast from 'components/toast';
import ShareModal from './ShareModal';
import SocialButton from './SocialButton';
import { Link } from 'react-router-dom/cjs/react-router-dom';

// Icons
import { ReactComponent as Calendar } from '../../assets/images/calendar_today.svg';
import { ReactComponent as Clock } from '../../assets/images/schedule.svg';
import { ReactComponent as User } from '../../assets/images/person_outline.svg';
import { ReactComponent as MapPin } from '../../assets/images/location_on.svg';
import { ReactComponent as Event } from '../../assets/images/festival.svg';
import { ReactComponent as Ticket } from '../../assets/images/confirmation_number.svg';

// Assets
import Logo from '../../assets/images/MdotMain.png';
import MainLogo from '../../assets/images/MOOKHMainLogo.png';
import Pattern from '../../assets/images/MookhPattern.png';
import './style.css';
import { position } from 'styled-system';

// iOS detection helper
const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const styles = {
  wrapper: {
    width: '100%'
  },
  main: {
    width: '100%',
    maxWidth: '500px',
    margin: '0 auto',
    borderRadius: '10px',
    overflow: 'hidden',
    padding: '20px',
    backgroundColor: '#31383e'
  },
  logo: {
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden'
  },
  ticketContainer: {
    width: '100%',
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    position: 'relative'
  },
  heading: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#31383e',
    textAlign: 'center',
    lineHeight: '24px',
    textTransform: 'uppercase',
    marginBottom: '8px'
  },
  qrCode: {
    width: '158px',
    height: '158px',
    margin: '0 auto',
    display: 'block'
  },
  ticketId: {
    fontSize: '24px',
    fontWeight: 800,
    color: '#31383e',
    textAlign: 'center',
    textTransform: 'uppercase',
    marginTop: '8px'
  },
  warningText: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#4b5258',
    textAlign: 'center',
    textTransform: 'uppercase',
    marginTop: '20px'
  },
  dividerContainer: {
    position: 'relative',
    margin: '16px -20px',
    height: '36px',
    display: 'flex',
    alignItems: 'center'
  },
  circle: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#31383e',
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)'
  },
  leftCircle: {
    left: '-18px'
  },
  rightCircle: {
    right: '-18px'
  },
  dottedLine: {
    flex: 1,
    borderTop: '2px dashed #31383e',
    margin: '0 18px'
  },
  ticketDetails: {
    marginTop: '24px',
    backgroundColor: '#31383e',
    padding: '28px',
    borderRadius: '8px',
    position: 'relative'
  },
  detailsGrid: {
    display: 'grid',
    gap: '24px'
  },
  detailRow: {
    display: 'flex',
    // flexWrap: 'wrap',
    // justifyContent: 'space-between',
    gap: '32px'
  },
  downloadLink: {
    display: 'block',
    textAlign: 'center',
    // marginTop: '20px',
    fontSize: '16px',
    fontWeight: 400,
    color: '#1f8de1'
  },
  shareButton: {
    width: '50%',
    textTransform: 'uppercase',
    fontWeight: '600',
    padding: '12px',
    backgroundColor: '#31383e !important',
    color: ' rgb(255, 236, 0) !important',
    border: 'none',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer'
  },
  additionalInfo: {
    marginTop: '24px'
  },
  infoHeading: {
    fontWeight: 400,
    color: '#31383e',
    fontSize: '16px',
    marginBottom: '20px',
    textAlign: 'center'
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '16px',
    color: '#31383e'
  },
  bullet: {
    marginRight: '8px'
  }
};

const detailStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  icon: {
    color: '#ffffff'
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    color: '#ffec00',
    fontSize: '14px',
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: '2px'
  },
  value: {
    color: '#ffffff',
    fontWeight: '800',
    textTransform: 'capitalize'
  }
};

const QRCode = styled.div`
  width: 158px;
  height: 158px;
  margin: 0 auto;
  display: block;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  image-rendering: -webkit-optimize-contrast;
`;
const TicketDetail = ({ icon, label, value }) => (
  <div style={detailStyles.container}>
    <div style={detailStyles.icon}>{icon}</div>
    <div style={detailStyles.textContainer}>
      <h3 style={detailStyles.label}>{label}</h3>
      <p
        style={{
          ...detailStyles.value,
          textDecoration: label === 'Location' ? 'underline' : ''
        }}
      >
        {value}
      </p>
    </div>
  </div>
);

const Divider = () => (
  <div style={styles.dividerContainer}>
    <div
      style={{
        ...styles.circle,
        ...styles.leftCircle
      }}
    />
    <div style={styles.dottedLine} />
    <div
      style={{
        ...styles.circle,
        ...styles.rightCircle
      }}
    />
  </div>
);

const EventTicket = ({ ticket }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const toast = useToast();
  const ticketRef = useRef(null);

  const downloadAsImage = async () => {
    setIsLoading(true);
    if (!ticketRef.current) return;

    try {
      const canvas = await html2canvas(ticketRef.current, {
        ignoreElements: element =>
          element.classList.contains('ticket-buttons-container'),
        onClone: clonedDoc => {
          clonedDoc.querySelectorAll('img').forEach(img => {
            const src = new URL(img.src);
            src.searchParams.set('timestamp', Date.now());
            img.src = src.toString();
            img.setAttribute('crossOrigin', 'anonymous');
          });

          clonedDoc.querySelectorAll('*').forEach(el => {
            const bgImage = window.getComputedStyle(el).backgroundImage;
            if (bgImage !== 'none' && !bgImage.includes('data:image')) {
              const url = bgImage.replace(/url\(['"]?(.*?)['"]?\)/, '$1');
              el.style.backgroundImage = `url('${url}?timestamp=${Date.now()}')`;
            }
          });
        },
        useCORS: true,
        scale: isIOS() ? 2 : 3,
        logging: true,
        backgroundColor: null
      });

      if (isIOS()) {
        const blob = await new Promise(resolve =>
          canvas.toBlob(resolve, 'image/jpeg', 0.9)
        );
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `mookh-ticket-${ticket?.ticket_number}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else {
        const link = document.createElement('a');
        link.download = `mookh-ticket-${ticket?.ticket_number}.jpg`;
        link.href = canvas.toDataURL('image/jpeg', 0.9);
        link.click();
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Error',
        description: isIOS()
          ? 'Press and hold the image to save. If download fails, please use Safari.'
          : 'Failed to download ticket',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
      setIsLoading(false);
    }
  };

  const generateTicketImage = async () => {
    setIsLoading(true);
    try {
      const canvas = await html2canvas(ticketRef.current, {
        useCORS: true,
        logging: true,
        scale: 2
      });

      const generatedUrl = canvas.toDataURL('image/png', 1.0);
      setImageUrl(generatedUrl);

      fetch(generatedUrl)
        .then(res => res.blob())
        .then(blob => {
          const url = URL.createObjectURL(blob);
          setImageUrl(url);
        });

      setIsOpen(true);
    } catch (error) {
      console.error('Error generating ticket image:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTime = date =>
    new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });

  const addToGoogleCalendar = () => {
    const location = window.location.href;
    const startDate = new Date(ticket?.start_datetime);
    const endDate = new Date(ticket?.stop_datetime);

    const googleCalendarUrl = new URL('https://www.google.com/calendar/render');
    googleCalendarUrl.searchParams.append('action', 'TEMPLATE');
    googleCalendarUrl.searchParams.append(
      'dates',
      `${startDate
        .toISOString()
        .replace(/-|:|\.\d\d\d/g, '')}/${endDate
        .toISOString()
        .replace(/-|:|\.\d\d\d/g, '')}`
    );
    googleCalendarUrl.searchParams.append('text', `${ticket?.reservable_name}`);
    googleCalendarUrl.searchParams.append(
      'details',
      `Ticket Link: \n${location} \nLocation: ${ticket?.venue} \n \n`
    );
    googleCalendarUrl.searchParams.append('location', `${ticket?.venue}`);

    window.open(googleCalendarUrl.href, '_blank');
  };

  const socials = [
    {
      type: 'facebook',
      label: 'Facebook',
      link: 'https://img.icons8.com/color/48/facebook-new.png',
      handler: () => {
        const message = encodeURIComponent('Check out my ticket!');
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${imageUrl}&quote=${message}`,
          '_blank'
        );
      }
    },
    {
      type: 'twitter',
      label: 'Twitter',
      link: 'https://img.icons8.com/color/48/twitter.png',
      handler: () => {
        const text = encodeURIComponent('Check out my ticket!');
        window.open(
          `https://twitter.com/intent/tweet?text=${text}&url=${imageUrl}`,
          '_blank'
        );
      }
    },
    {
      type: 'whatsapp',
      label: 'WhatsApp',
      link: 'https://img.icons8.com/color/48/whatsapp.png',
      handler: () => {
        const text = encodeURIComponent('Check out my ticket! ');
        window.open(`https://wa.me/?text=${text}${imageUrl}`, '_blank');
      }
    },
    {
      type: 'email',
      label: 'Email',
      link: 'https://img.icons8.com/color/48/email.png',
      handler: () => {
        const subject = encodeURIComponent('My Event Ticket');
        const body = encodeURIComponent(
          `Here's my ticket! \n\n![Ticket](${imageUrl})`
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      }
    }
  ];

  return (
    <div style={styles.wrapper}>
      <main style={styles.main} ref={ticketRef} className="main">
        <div style={styles.logo}>
          <img
            src={MainLogo}
            alt="Mookh logo"
            style={{ marginBottom: '16px', height: '38px', width: '170px' }}
          />
        </div>

        <div style={styles.ticketContainer}>
          <h3 style={styles.heading}>{ticket?.ticket_name}</h3>
          <QRCode src={ticket?.qr_code} />
          <h3 style={styles.ticketId}>{ticket?.ticket_number}</h3>
          <h4
            style={{
              fontSize: '20px',
              textAlign: 'center',
              fontWeight: '800'
            }}
          >
            {ticket?.order_number}
          </h4>
          <h4 style={styles.warningText}>Do not share this QR code</h4>

          <Divider />

          <div style={styles.ticketDetails}>
            <img
              src={Logo}
              alt="Mookh logo"
              style={{ marginBottom: '16px', height: '26px', width: '26px' }}
            />
            <img
              src={Pattern}
              alt="Mookh logo"
              style={{
                marginBottom: '16px',
                position: 'absolute',
                right: '0',
                bottom: '-12px'
              }}
            />
            <div style={styles.detailsGrid}>
              <TicketDetail
                icon={<User style={{ color: '#dece12' }} />}
                label="Attendee"
                value={ticket?.customer}
              />
              <TicketDetail
                icon={<Ticket style={{ color: '#dece12' }} />}
                label="Ticket"
                value={ticket?.ticket_name}
              />
              <TicketDetail
                icon={<Event />}
                label="EVENT"
                value={ticket?.reservable_name}
              />
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${ticket?.venue}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <TicketDetail
                  icon={<MapPin />}
                  label="Location"
                  value={ticket?.venue}
                />
              </a>

              <table>
                <tr>
                  <td>
                    <TicketDetail
                      icon={<Calendar />}
                      label="Start"
                      value={ticket?.start_date}
                    />
                  </td>
                  <td>
                    <TicketDetail
                      icon={<Clock />}
                      label="Time"
                      value={getTime(ticket?.start_datetime)}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <div style={{ marginTop: '20px' }}>
                      <TicketDetail
                        icon={<Calendar />}
                        label="End"
                        value={ticket?.stop_date}
                      />
                    </div>
                  </td>
                  <td>
                    <TicketDetail
                      icon={<Clock />}
                      label="Time"
                      value={getTime(ticket?.stop_datetime)}
                    />
                  </td>
                </tr>
              </table>
            </div>
          </div>

          <div className="ticket-buttons-container">
            <div className="ticket-buttons" style={{ width: '100%' }}>
              <button
                onClick={downloadAsImage}
                type="button"
                className="mookh-button"
                disabled={isLoading}
                style={{ width: '100%', margin: 0 }}
              >
                {isLoading ? (
                  <span>{isIOS() ? 'Preparing...' : 'Downloading...'}</span>
                ) : (
                  <span>Download Ticket</span>
                )}
              </button>
            </div>
            <div>
              <a
                onClick={addToGoogleCalendar}
                href="/"
                style={styles.downloadLink}
              >
                Add To Calendar
              </a>
            </div>
          </div>

          <Divider />

          {ticket?.ticket_description && (
            <div style={styles.additionalInfo}>
              <h3 style={styles.infoHeading}>Additional Info:</h3>
              <div
                dangerouslySetInnerHTML={{
                  __html: ticket?.ticket_description || ''
                }}
              />
            </div>
          )}
        </div>
      </main>

      <ShareModal setIsOpen={setIsOpen} isOpen={isOpen}>
        <div
          style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'space-between',
            padding: '16px 0'
          }}
        >
          {socials.map((social, index) => (
            <SocialButton
              key={index}
              type={social.type}
              label={social.label}
              link={social.link}
              onClick={social.handler}
            />
          ))}
        </div>
        {isIOS() && imageUrl && (
          <div className="ios-preview">
            <img
              src={imageUrl}
              alt="Ticket preview"
              style={{ maxWidth: '300px', marginTop: '20px' }}
            />
            <p className="ios-instructions">Press and hold to save image</p>
          </div>
        )}

        {isLoading && isIOS() && (
          <div className="ios-loading-overlay">
            <div className="ios-loading-spinner" />
            <p>
              Preparing image...
              <br />
              <small>This may take longer on iOS</small>
            </p>
          </div>
        )}
      </ShareModal>
    </div>
  );
};

// Keep your existing Divider and TicketDetail components unchanged

export default EventTicket;
